import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getProperty } from '../api/properties-api';
import { getBuildings } from '@/features/buildings/api/buildings-api';
import { getUnits } from '@/features/units/api/units-api';
import { getDevices, type DeviceSortBy, type SortDir } from '@/features/devices/api/devices-api';
import { usePagePolling, POLL_INTERVAL_DETAIL_MS } from '@/lib/use-page-polling';
import type { Property, Building, Unit, Device, PaginatedResponse } from '@/types/api';


// Per-page choices the property device table offers. 25 is a good middle
// default; power users on big properties bump to 100. Choice persists in
// localStorage so the user's preference sticks across sessions.
export const DEVICE_PER_PAGE_OPTIONS = [10, 25, 50, 100] as const;
export type DevicePerPage = typeof DEVICE_PER_PAGE_OPTIONS[number];
const DEFAULT_PER_PAGE: DevicePerPage = 25;
const PER_PAGE_STORAGE_KEY = 'norgas_property_devices_per_page';

// Sort preference is also sticky — installers tend to settle on one ordering
// (usually Unit ↑) and shouldn't have to re-pick on every visit.
const SORT_BY_STORAGE_KEY = 'norgas_property_devices_sort_by';
const SORT_DIR_STORAGE_KEY = 'norgas_property_devices_sort_dir';
const DEFAULT_SORT_BY: DeviceSortBy = 'unit';
const DEFAULT_SORT_DIR: SortDir = 'asc';

function readStoredPerPage(): DevicePerPage {
  if (typeof window === 'undefined') return DEFAULT_PER_PAGE;
  const raw = window.localStorage.getItem(PER_PAGE_STORAGE_KEY);
  const parsed = raw ? Number(raw) : NaN;
  return DEVICE_PER_PAGE_OPTIONS.includes(parsed as DevicePerPage)
    ? (parsed as DevicePerPage)
    : DEFAULT_PER_PAGE;
}

function readStoredSortBy(): DeviceSortBy {
  if (typeof window === 'undefined') return DEFAULT_SORT_BY;
  const raw = window.localStorage.getItem(SORT_BY_STORAGE_KEY);
  return raw === 'unit' || raw === 'createdAt' ? raw : DEFAULT_SORT_BY;
}

function readStoredSortDir(): SortDir {
  if (typeof window === 'undefined') return DEFAULT_SORT_DIR;
  const raw = window.localStorage.getItem(SORT_DIR_STORAGE_KEY);
  return raw === 'asc' || raw === 'desc' ? raw : DEFAULT_SORT_DIR;
}

export function usePropertyPortal() {
  const { propertyId } = useParams<{ propertyId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);

  const [buildings, setBuildings] = useState<Building[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);

  // Device table state
  const [devicesResponse, setDevicesResponse] = useState<PaginatedResponse<Device> | null>(null);
  const [devicesLoading, setDevicesLoading] = useState(true);

  // Map devices are property-scoped (not filtered by sidebar) so the map
  // stays stable while the installer clicks through buildings/units. The
  // table has its own filtered fetch above.
  const [mapDevices, setMapDevices] = useState<Device[]>([]);

  // URL-driven Building/Unit filter — same `?buildingId=` / `?unitId=`
  // params drive both Setup Mode's initial building (preserves the
  // previous behaviour) AND the new filter dropdowns above the device
  // table. Sharing the param keeps emailed deep links and bookmarks
  // pointing at the same property scope after the sidebar tree was
  // removed.
  const initialBuildingId = searchParams.get('buildingId') ?? '';
  const initialUnitId = searchParams.get('unitId') ?? '';

  // Filters from URL
  const [selectedBuildingId, setSelectedBuildingId] = useState(initialBuildingId);
  const [selectedUnitId, setSelectedUnitId] = useState(initialUnitId);
  const [devicesPage, setDevicesPage] = useState(1);
  const [devicesPerPage, setDevicesPerPageState] = useState<DevicePerPage>(() => readStoredPerPage());
  const [devicesSortBy, setDevicesSortByState] = useState<DeviceSortBy>(() => readStoredSortBy());
  const [devicesSortDir, setDevicesSortDirState] = useState<SortDir>(() => readStoredSortDir());

  const setDevicesPerPage = useCallback((next: DevicePerPage) => {
    setDevicesPerPageState(next);
    setDevicesPage(1);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(PER_PAGE_STORAGE_KEY, String(next));
    }
  }, []);

  // Sort changes always reset to page 1 — staying on page 3 with a different
  // ordering would dump the user into an unrelated slice of the list.
  const setDevicesSortBy = useCallback((next: DeviceSortBy) => {
    setDevicesSortByState(next);
    setDevicesPage(1);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(SORT_BY_STORAGE_KEY, next);
    }
  }, []);

  const setDevicesSortDir = useCallback((next: SortDir) => {
    setDevicesSortDirState(next);
    setDevicesPage(1);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(SORT_DIR_STORAGE_KEY, next);
    }
  }, []);

  // Setup Mode is driven purely by the URL now. A session can start from the
  // property portal with "All" selected — the panel itself surfaces a building
  // picker until the installer commits to one.
  const isSetupMode = searchParams.get('mode') === 'setup';

  // Push (don't replace) when entering setup mode so browser back naturally
  // pops the ?mode=setup param and returns the user to the property they
  // were viewing — without leaving the page or the property context.
  // Mike's "back means back to where I was" expectation depends on this:
  // before, replace: true meant back skipped past the property entirely
  // and landed on /properties.
  const enterSetupMode = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('mode', 'setup');
      if (selectedBuildingId) next.set('buildingId', selectedBuildingId);
      return next;
    });
  }, [selectedBuildingId, setSearchParams]);

  const exitSetupMode = useCallback(() => {
    // iOS-Safari mitigation: if a Setup Mode input still has focus when
    // the panel unmounts, the on-screen keyboard collapses *after* the
    // property page has already laid itself out against the keyboard-open
    // viewport. The result QA reported: "the bottom gets cut off when
    // switching out of setup mode" — the property page renders into the
    // smaller-than-actual viewport and the bottom slice never reflows
    // until the user scrolls/taps. Dismiss the keyboard first by blurring
    // the active element, then nudge a layout recomputation on the next
    // animation frame after iOS has restored the viewport.
    if (typeof document !== 'undefined') {
      (document.activeElement as HTMLElement | null)?.blur?.();
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('mode');
      next.delete('buildingId');
      return next;
    }, { replace: true });
    if (typeof window !== 'undefined') {
      // Two RAFs: the first gives iOS a beat to close the keyboard and
      // restore the layout viewport; the second dispatches a synthetic
      // resize that forces the flex/overflow chain (h-dvh root → main
      // overflow-y-auto) to recompute against the now-correct height.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.dispatchEvent(new Event('resize'));
          // Belt-and-suspenders: scroll any overflow-auto ancestors
          // back to top so the user lands at the property header
          // rather than wherever iOS scrolled to during keyboard open.
          window.scrollTo({ top: 0, behavior: 'auto' });
          document.querySelectorAll<HTMLElement>('main').forEach((el) => {
            el.scrollTop = 0;
          });
        });
      });
    }
  }, [setSearchParams]);

  // Changing the building during a Setup Mode session has two meanings:
  //   - from "All"/nothing into a specific building: locks the session to it
  //   - between two different buildings: the installer has moved on, exit
  // Outside Setup Mode the change is a pure filter — URL params are
  // updated so deep linking + back/forward navigation work.
  const changeBuilding = useCallback((id: string) => {
    const switchingSession = isSetupMode && selectedBuildingId && id && id !== selectedBuildingId;
    setSelectedBuildingId(id);
    setSelectedUnitId('');
    setDevicesPage(1);
    if (switchingSession) {
      exitSetupMode();
      return;
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set('buildingId', id); else next.delete('buildingId');
      // Clearing/changing the building always nukes the unit filter — the
      // unit list narrows to the building, so a stale unit param would
      // never match.
      next.delete('unitId');
      return next;
    }, { replace: true });
  }, [isSetupMode, selectedBuildingId, exitSetupMode, setSearchParams]);

  // selectUnit sets both building + unit atomically. The previous "reset
  // unit when building changes" effect raced with this and forced a
  // second click to land on the unit.
  const selectUnit = useCallback((buildingId: string, unitId: string) => {
    const switchingSession = isSetupMode && selectedBuildingId && buildingId !== selectedBuildingId;
    setSelectedBuildingId(buildingId);
    setSelectedUnitId(unitId);
    setDevicesPage(1);
    if (switchingSession) {
      exitSetupMode();
      return;
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (buildingId) next.set('buildingId', buildingId); else next.delete('buildingId');
      if (unitId) next.set('unitId', unitId); else next.delete('unitId');
      return next;
    }, { replace: true });
  }, [isSetupMode, selectedBuildingId, exitSetupMode, setSearchParams]);

  // Single-building auto-select: when an installer enters Setup Mode on a
  // property with exactly one building, skip the picker pre-screen. The Change
  // link in the panel header provides an escape hatch only when there's
  // somewhere to switch to (handled in setup-mode-panel.tsx).
  useEffect(() => {
    if (!isSetupMode) return;
    if (selectedBuildingId) return;
    if (buildings.length !== 1) return;
    changeBuilding(buildings[0].id);
  }, [isSetupMode, selectedBuildingId, buildings, changeBuilding]);

  // Public unit-filter setter — writes both component state AND the
  // ?unitId= URL param so deep linking, browser back/forward, and the
  // device-row breadcrumbs stay in sync. Always reset to page 1 since
  // changing the unit filter dumps you into a different slice.
  const setSelectedUnitIdSynced = useCallback((id: string) => {
    setSelectedUnitId(id);
    setDevicesPage(1);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set('unitId', id); else next.delete('unitId');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const [refreshCount, setRefreshCount] = useState(0);
  const refresh = useCallback(() => setRefreshCount((n) => n + 1), []);

  // Load property details
  useEffect(() => {
    if (!propertyId) return;
    setLoading(true);
    getProperty(propertyId)
      .then((res) => { if (res.data) setProperty(res.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [propertyId, refreshCount]);

  // Load buildings for the sidebar tree.
  const fetchBuildings = useCallback(async () => {
    if (!propertyId) return;
    try {
      const res = await getBuildings({ propertyId, perPage: 100 });
      setBuildings(res.items);
    } catch {
      // Background fetches swallow transient errors; next tick retries.
    }
  }, [propertyId]);

  useEffect(() => { void fetchBuildings(); }, [fetchBuildings, refreshCount]);

  // Load all units for the sidebar tree (grouped by building).
  const fetchUnits = useCallback(async (buildingsForFetch: Building[]) => {
    if (!propertyId || buildingsForFetch.length === 0) {
      setUnits([]);
      return;
    }
    try {
      const results = await Promise.all(
        buildingsForFetch.map((b) => getUnits({ buildingId: b.id, perPage: 100 })),
      );
      setUnits(results.flatMap((r) => r.items));
    } catch {
      // Background fetches swallow transient errors; next tick retries.
    }
  }, [propertyId]);

  useEffect(() => { void fetchUnits(buildings); }, [fetchUnits, buildings]);

  // Property-wide devices for the map (static across sidebar filter changes).
  const fetchMapDevices = useCallback(async () => {
    if (!propertyId) return;
    try {
      const res = await getDevices({ propertyId, perPage: 1000 });
      setMapDevices(res.items);
    } catch {
      // Background fetches swallow transient errors; next tick retries.
    }
  }, [propertyId]);

  useEffect(() => {
    let cancelled = false;
    void fetchMapDevices().then(() => { if (cancelled) { /* no-op */ } });
    return () => { cancelled = true; };
  }, [fetchMapDevices, refreshCount]);

  // Load devices — single API call with optional building/unit filter.
  // silent:true skips the loading spinner so the 5s background poll doesn't flash.
  const fetchDevices = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!propertyId) return;
    if (!silent) setDevicesLoading(true);
    try {
      const res = await getDevices({
        propertyId,
        buildingId: selectedBuildingId || undefined,
        unitId: selectedUnitId || undefined,
        page: devicesPage,
        perPage: devicesPerPage,
        sortBy: devicesSortBy,
        sortDir: devicesSortDir,
      });
      setDevicesResponse(res);
    } catch {
      if (!silent) setDevicesResponse(null);
    } finally {
      if (!silent) setDevicesLoading(false);
    }
  }, [propertyId, selectedBuildingId, selectedUnitId, devicesPage, devicesPerPage, devicesSortBy, devicesSortDir]);

  useEffect(() => {
    void fetchDevices();
  }, [fetchDevices, refreshCount]);

  // Background poll: visibility-gated detail-page cadence so the device
  // list, map markers, AND the building/unit tree reflect mutations
  // from other tabs / other users without a manual refresh. (Buildings
  // were previously loaded once on mount — QA caught the gap when
  // adding a building from desktop while viewing the property on
  // mobile didn't update.) Setup Mode runs its own faster cadence
  // (useSetupModePolling) and is intentionally not standardized here.
  usePagePolling(
    () => {
      void fetchMapDevices();
      void fetchDevices({ silent: true });
      void fetchBuildings();
      // Units fetch is keyed off the latest buildings array; it'll
      // re-fire automatically when fetchBuildings produces a new list.
    },
    POLL_INTERVAL_DETAIL_MS,
    { enabled: !!propertyId },
  );

  const devices = devicesResponse?.items ?? [];
  const devicesTotalCount = devicesResponse?.totalCount ?? 0;
  const devicesTotalPages = Math.max(1, Math.ceil(devicesTotalCount / devicesPerPage));

  return {
    propertyId,
    property,
    loading,
    buildings,
    units,
    devices,
    mapDevices,
    devicesLoading,
    devicesTotalCount,
    devicesTotalPages,
    devicesPage,
    setDevicesPage,
    devicesPerPage,
    setDevicesPerPage,
    devicesSortBy,
    setDevicesSortBy,
    devicesSortDir,
    setDevicesSortDir,
    selectedBuildingId,
    setSelectedBuildingId,
    changeBuilding,
    selectUnit,
    selectedUnitId,
    setSelectedUnitId: setSelectedUnitIdSynced,
    refresh,
    isSetupMode,
    enterSetupMode,
    exitSetupMode,
  };
}
